import {
  Accordion as MUIAccordion,
  AccordionBody as MUIAccordionBody,
  AccordionHeader as MUIAccordionHeader,
} from "@material-tailwind/react";
import { type PropsWithChildren, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AccordionIcon } from "../../helpers/componentHelpers";
import { Stateful, useStateful } from "../../hooks/useStateful";
import AlertDefault, { AlertVariant } from "../Alerts/AlertDefault";
import CountCircle from "../CountCirle";
import { AlertIcon, ErrorIcon } from "../Icons/Icons";
import Text, { Header, TextVariant } from "../Text/Text";

export type AccordionDefaultProps = PropsWithChildren & {
  title: string;
  details?: string;
  defaultOpen?: boolean;
  isOpen?: Stateful<boolean>;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  count?: number;
  info?: string;
  cardColor?: string;
  useErrorIcon?: boolean; //to use triangle error icon instead of circular alert icon
};
export function AccordionDefault({
  title,
  details,
  children,
  defaultOpen = true,
  isOpen = undefined,
  disabled = false,
  errorMessage,
  error = false,
  count,
  info,
  cardColor,
  useErrorIcon,
}: AccordionDefaultProps): JSX.Element {
  const _isOpen = useStateful<boolean>(defaultOpen);
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen !== undefined && isOpen.value != _isOpen.value) {
      _isOpen.set(isOpen?.value);
    }
  }, [isOpen, isOpen?.value]);

  useEffect(() => {
    const debounceSetOuter = setTimeout(() => {
      if (isOpen !== undefined && isOpen.value != _isOpen.value) {
        isOpen.set(_isOpen.value);
      }
    }, 50);
    return () => {
      clearTimeout(debounceSetOuter);
    };
  }, [_isOpen.value]);

  return (
    <MUIAccordion
      open={_isOpen.value && !disabled}
      disabled={disabled}
      icon={
        <AccordionIcon
          isOpen={_isOpen.value && !disabled}
          error={error || errorMessage != undefined}
        />
      }
      className={`${
        _isOpen.value && (error || errorMessage)
          ? "bg-ldir-red-900"
          : error || errorMessage
            ? "bg-ldir-red-700"
            : _isOpen.value
              ? "bg-ldir-blue-800"
              : "bg-ldir-blue-100"
      } border-t ${
        error || errorMessage
          ? "border-ldir-red-900"
          : _isOpen.value && !disabled
            ? "border-ldir-blue-900"
            : "border-ldir-blue-pale"
      }`}
    >
      <MUIAccordionHeader
        onClick={() => _isOpen.set(!_isOpen.value)}
        className={`font-normal border-b-0 ${
          _isOpen.value
            ? `${
                error || errorMessage
                  ? "text-ldir-red-700 hover:text-ldir-red-700"
                  : "text-ldir-blue-100 hover:text-ldir-blue-100"
              }`
            : "text-ldir-brown-900"
        } py-5 items-start`}
      >
        <div className="w-full ml-4">
          <Header headerLvl={2} variant={TextVariant.large}>
            {title}
          </Header>
          {details && (
            <Text variant={TextVariant.large} className="mt-1.5">
              {details}
            </Text>
          )}
        </div>
        <div className=" flex items-center">
          {(error || errorMessage) &&
            (useErrorIcon ? (
              <ErrorIcon
                sizeClassName={"h-7 w-7"}
                title={t("components.accordion.error")}
                className={`${_isOpen.value ? "text-ldir-red-700" : "text-ldir-red-900"}`}
              />
            ) : (
              <AlertIcon
                sizeClassName={"h-7 w-7"}
                title={t("components.accordion.error")}
                className={`${_isOpen.value ? "text-ldir-red-700" : "text-ldir-red-900"}`}
              />
            ))}
          {count != undefined && (
            <CountCircle
              count={count}
              color={!_isOpen.value ? "dark" : error || errorMessage ? "errorLight" : "light"}
              className="text-xl ml-2"
            />
          )}
        </div>
      </MUIAccordionHeader>
      <div aria-hidden={!_isOpen.value}>
        <MUIAccordionBody
          className={` ${cardColor ?? "bg-ldir-brown-300 "} text-ldir-brown-900 py-0 font-normal`}
        >
          {info && <AlertDefault variant={AlertVariant.Info}>{info}</AlertDefault>}
          {errorMessage && <AlertDefault variant={AlertVariant.Error}>{errorMessage}</AlertDefault>}
          {_isOpen.value && <div className="py-4">{children}</div>}
        </MUIAccordionBody>
      </div>
    </MUIAccordion>
  );
}
